import { forwardRef, useState, useEffect, useMemo, useRef } from "react";
import type { Ref, ChangeEvent, ReactNode, ForwardedRef } from "react";
import type { MenuPosition, MenuPlacement } from "react-select";
import { cx } from "../../../utils";
import { Label } from "../Label";
import { InputSize, getDaysInMonth } from "./utils";
import { DayInput } from "./day-input";
import { MonthSelect } from "./month-select";
import { YearInput } from "./year-input";

interface InputProps {
  label?: string | ReactNode;
  size?: InputSize;
  id: string;
  placeholder?: string;
  type?: string;
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  className?: string;
  error?: string;
  maxLength?: number;
  max?: number;
  checked?: boolean;
  focusOnError?: boolean;
  defaultValue?: string;
  borderLess?: boolean;
  readOnly?: boolean;
  list?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  /**
   * Renders a Clear action that empties the field. Opt-in: the month dropdown has
   * no empty option, so without it the only way back to an empty date is wiping
   * the day or year box, which leaves a stale month/year on screen.
   */
  clearable?: boolean;
  /** Renders a trailing `*` on the label. */
  showRequired?: boolean;
  hideOptional?: boolean;
  ignoreDay?: boolean;
  locale?: string;
  legacyErrorMargins?: boolean;
  /** `false` keeps the red state but lets the caller place the message. */
  showErrorMessage?: boolean;

  // For month dropdown
  menuPosition?: MenuPosition;
  menuPlacement?: MenuPlacement;
  monthSearchable?: boolean;
  monthMenuPortalTarget?: HTMLElement | null;
}

const translateInvalidDate = (locale: string) => {
  switch (locale) {
    case "es":
      return "Fecha inválida";
    case "en":
    default:
      return "Invalid date";
  }
};

const translateClear = (locale: string) => {
  switch (locale) {
    case "es":
      return "Borrar fecha";
    case "en":
    default:
      return "Clear date";
  }
};

type DateParts = { day: string; month: string; year: string };

const hasAnyPart = ({ day, month, year }: DateParts) =>
  Boolean(day || month || year);

const transformedDay = (dayValue: string, ignoreDay: boolean) => {
  if (ignoreDay) return "01";
  if (!dayValue) return "";
  return dayValue.length === 2 ? dayValue : dayValue.padStart(2, "0");
};

/** `YYYY-MM-DD` for complete, valid parts; `""` otherwise (e.g. 31 February). */
const composeIso = ({ day, month, year }: DateParts, ignoreDay: boolean) => {
  if (!year || !month || (!day && !ignoreDay)) return "";
  if (!ignoreDay && Number(day) > getDaysInMonth(Number(year), Number(month)))
    return "";

  const date = new Date(
    Number(year),
    Number(month) - 1,
    ignoreDay ? 1 : Number(day)
  );
  if (date instanceof Date && !isNaN(Number(date))) {
    return `${year}-${month}-${transformedDay(day, ignoreDay)}`;
  }

  return "";
};

// Prefill on this won't work.

const DateInputComponent = (
  props: InputProps,
  ref?: ForwardedRef<HTMLInputElement>
) => {
  const {
    label,
    id,
    // placeholder,
    // type,
    size = InputSize.BASE,
    onChange,
    className,
    error,
    value,
    readOnly,
    focusOnError,
    borderLess,
    ignoreDay,
    required,
    showRequired,
    hideOptional,
    clearable,
    locale = "en",
    menuPosition,
    menuPlacement,
    monthSearchable,
    monthMenuPortalTarget,
    legacyErrorMargins = true,
    showErrorMessage = true
    // ...rest
  } = props;

  const yearRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (focusOnError && error) {
      (
        yearRef as {
          current?: {
            focus?: () => void;
          };
        }
      ).current?.focus?.();
    }
  }, [focusOnError, error]);

  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  /**
   * Last value handed to `onChange`. A `value` prop that merely echoes it must
   * not reset the parts: with fast typing the parent's state can lag a
   * keystroke behind, and re-applying that stale echo used to ping-pong the
   * year between two values forever.
   */
  const lastEmitted = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (typeof value === "string" && value) {
      if (value === lastEmitted.current) return;
      // trim T part from ISO date
      const date = value.includes("T") ? value.split("T")[0] : value;
      const [y, m, d] = date.includes("-") ? date.split("-") : date.split("/");

      setYear(y);
      setMonth(m);
      setDay(ignoreDay ? "01" : parseInt(d).toString());
    } else if (clearable && !value) {
      // Follow a parent that empties the value, or the boxes keep showing a date the
      // form no longer holds. Only for clearable fields: elsewhere a partial composite
      // still round-trips as empty, and following that would blank untouched boxes.
      setYear("");
      setMonth("");
      setDay("");
    }
  }, [value]);

  // Validate day - like 30 february / 31 september
  const isValidDay = useMemo(() => {
    if (ignoreDay) return true;
    // Ignore check if month or year is not set
    if (!month || !year) return true;
    return Number(day) <= getDaysInMonth(Number(year), Number(month));
  }, [day, month, year, ignoreDay]);

  const isoDate = useMemo(
    () => composeIso({ day, month, year }, Boolean(ignoreDay)),
    [day, month, year, ignoreDay]
  );

  const emit = (iso: string) => {
    if (iso === lastEmitted.current) return;
    lastEmitted.current = iso;
    onChange?.({
      // @ts-expect-error -- well, that's fine
      target: { id, value: iso } as unknown as EventTarget
    });
  };

  /**
   * Updates one part and notifies the parent in the same event, so the
   * parent's state is flushed before the next keystroke can arrive.
   */
  const changePart = (patch: Partial<DateParts>) => {
    const next = { day, month, year, ...patch };
    setDay(next.day);
    setMonth(next.month);
    setYear(next.year);

    const iso = composeIso(next, Boolean(ignoreDay));
    // A partly filled or invalid composite is a date still being typed, not an
    // erase, so a clearable field holds its previous value until the boxes are
    // complete or all empty — only Clear reaches the form as empty.
    // Non-clearable fields emit the composite exactly as before.
    if (clearable && !iso && hasAnyPart(next)) return;
    emit(iso);
  };

  const clearParts = () => {
    setDay("");
    setMonth("");
    setYear("");
    emit("");
  };

  return (
    <>
      <input id={id} name={id} ref={ref} type="hidden" value={isoDate} />

      {!borderLess && label ? (
        <Label
          htmlFor={id}
          locale={locale}
          showRequired={showRequired}
          showOptional={Boolean(!required && !hideOptional)}
        >
          {label}
        </Label>
      ) : null}

      <div className={cx("flex gap-2", className)}>
        <MonthSelect
          menuPosition={menuPosition}
          menuPlacement={menuPlacement}
          searchable={monthSearchable}
          menuPortalTarget={monthMenuPortalTarget}
          locale={locale}
          error={Boolean(error || !isValidDay)}
          onChange={(next) => changePart({ month: next })}
          onFocus={() => {
            yearRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
          }}
          readOnly={readOnly}
          legacyErrorMargins={legacyErrorMargins}
          size={size}
          value={month}
        />
        {ignoreDay ? null : (
          <DayInput
            locale={locale}
            error={Boolean(error || !isValidDay)}
            onChange={(next) => changePart({ day: next })}
            readOnly={readOnly}
            size={size}
            value={day}
            legacyErrorMargins={legacyErrorMargins}
          />
        )}
        <YearInput
          locale={locale}
          error={Boolean(error || !isValidDay)}
          onChange={(next) => changePart({ year: next })}
          readOnly={readOnly}
          ref={yearRef as Ref<HTMLInputElement>}
          size={size}
          value={year}
          legacyErrorMargins={legacyErrorMargins}
        />
      </div>

      {clearable && !readOnly && hasAnyPart({ day, month, year }) ? (
        <button
          className="text-content-300 hover:text-content-1000 text-xs font-medium underline"
          onClick={clearParts}
          type="button"
        >
          {translateClear(locale)}
        </button>
      ) : null}

      {(showErrorMessage && error) || !isValidDay ? (
        <p className="text-danger-500 text-xs mt-2">
          {!isValidDay ? translateInvalidDate(locale) : error}
        </p>
      ) : null}
    </>
  );
};

const DateInput = forwardRef(DateInputComponent);
DateInput.displayName = "DateInput";

export { DateInput, InputSize };
